/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router';
import { seoConfig, getCanonicalUrl } from './seo-config';
import type { SupportedLanguage } from './seo.types';

interface HreflangTagsProps {
  /**
   * Custom path to use instead of the current location.
   * Useful when the same content is available at different paths.
   */
  customPath?: string;
  /**
   * The default language for x-default hreflang tag.
   * Defaults to the site's default language from config.
   */
  defaultLanguage?: SupportedLanguage;
}

/**
 * HreflangTags component for international SEO.
 * 
 * This component generates hreflang link tags for all supported languages,
 * helping search engines understand the relationship between language variants
 * of the same content.
 * 
 * @example
 * ```tsx
 * // Basic usage - uses current URL path
 * <HreflangTags />
 * 
 * // With custom path
 * <HreflangTags customPath="/products" />
 * ```
 */
export function HreflangTags({ 
  customPath,
  defaultLanguage = seoConfig.defaultLanguage,
}: HreflangTagsProps = {}) {
  const location = useLocation();
  
  // Use custom path or current location
  const path = customPath || location.pathname;
  
  // Generate URLs for each supported language
  // Note: In this implementation, we use the same URL for all languages
  // since the app uses client-side language detection.
  // In a more advanced setup, you might have different URL paths per language.
  const canonicalUrl = getCanonicalUrl(path);
  
  return (
    <Helmet>
      {/* Hreflang tags for each supported language */}
      {seoConfig.supportedLanguages.map((lang) => (
        <link
          key={`hreflang-${lang}`}
          rel="alternate"
          hrefLang={lang}
          href={canonicalUrl}
        />
      ))}
      
      {/* x-default tag for users without a matching language preference */}
      <link
        rel="alternate"
        hrefLang="x-default"
        href={canonicalUrl}
      />
    </Helmet>
  );
}

export default HreflangTags;
